import { useState, useMemo, useCallback } from 'react'
import { Filter, Loader2, X } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import { DagFilterExpressionBuilder } from './DagFilterExpressionBuilder'
import {
  EMPTY_FILTER_GROUP,
  buildDagFieldMeta,
  countConditions,
  serializeDagFilterGroup,
} from './dagFilterUtils'
import { useHierarchyForDag } from '@/hooks/useHierarchyForDag'
import type { FilterGroup } from '@/types'
import type { DagColumnMeta, DropdownOptionRead } from '@/types/dag'

interface LineItemFilterButtonProps {
  columns: DagColumnMeta[]
  dropdownMap: Record<string, DropdownOptionRead[]>
  filter: FilterGroup | null
  onApplyFilter: (filter: FilterGroup | null) => void
  /** True while a filter-triggered row fetch is in flight, so the trigger button can show feedback instead of looking stuck. */
  isApplying?: boolean
}

/**
 * Self-contained "Filters" trigger + popover for the line items table,
 * mirroring ColumnSelectorPopover's pattern (owns its own open state,
 * commits changes only on an explicit action). Edits are kept in a local
 * draft and only pushed up (triggering a new search request) on Apply, so
 * partial edits to a multi-rule filter don't fire requests mid-edit.
 */
export function LineItemFilterButton({
  columns,
  dropdownMap,
  filter,
  onApplyFilter,
  isApplying,
}: LineItemFilterButtonProps) {
  const [open, setOpen] = useState(false)
  const [draft, setDraft] = useState<FilterGroup>(filter ?? EMPTY_FILTER_GROUP)

  const hierarchyData = useHierarchyForDag()
  const hierarchyOptions = useMemo(
    () => ({
      division: hierarchyData?.divisionOptions,
      department: hierarchyData?.departmentOptions,
      class: hierarchyData?.classOptions,
      subclass: hierarchyData?.subclassOptions,
    }),
    [hierarchyData]
  )

  const fieldMeta = useMemo(
    () => buildDagFieldMeta(columns, dropdownMap, hierarchyOptions),
    [columns, dropdownMap, hierarchyOptions]
  )
  const fieldOrder = useMemo(() => columns.map(c => c.id), [columns])

  const activeCount = useMemo(() => countConditions(filter), [filter])

  const handleOpenChange = useCallback(
    (next: boolean) => {
      if (next) setDraft(filter ?? EMPTY_FILTER_GROUP)
      setOpen(next)
    },
    [filter]
  )

  const handleApply = () => {
    const serialized = serializeDagFilterGroup(draft, fieldMeta)
    onApplyFilter(countConditions(serialized) > 0 ? serialized : null)
    setOpen(false)
  }

  const handleClearAll = () => {
    setDraft(EMPTY_FILTER_GROUP)
    onApplyFilter(null)
    setOpen(false)
  }

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <Button variant="outline" className="gap-2 cursor-pointer">
          {isApplying ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <Filter className="h-4 w-4" />
          )}
          Filters
          {activeCount > 0 && (
            <Badge className="h-5 min-w-5 justify-center px-1.5 py-0">
              {activeCount}
            </Badge>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        className="flex w-[640px] max-h-[min(38rem,var(--radix-popover-content-available-height,38rem))] flex-col p-3"
      >
        <div className="flex items-center mb-2 shrink-0">
          <span className="flex items-center gap-1.5 text-sm font-semibold">
            <Filter className="h-3.5 w-3.5" />
            Filter Options
          </span>
        </div>

        <div className="flex-1 min-h-0 overflow-y-auto">
          <DagFilterExpressionBuilder
            group={draft}
            onChange={setDraft}
            fieldMeta={fieldMeta}
            fieldOrder={fieldOrder}
          />
        </div>

        <div className="flex justify-between gap-2 mt-3 shrink-0">
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="gap-1 cursor-pointer"
            onClick={handleClearAll}
            disabled={activeCount === 0}
          >
            <X className="h-3.5 w-3.5" />
            Clear Filters
          </Button>
          <div className="flex gap-2">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="cursor-pointer"
              onClick={() => setOpen(false)}
            >
              Cancel
            </Button>
            <Button
              type="button"
              size="sm"
              className="cursor-pointer"
              onClick={handleApply}
            >
              Apply Filters
            </Button>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  )
}
